import React, { useState, useEffect, useRef } from 'react';
import PageThumbnail from './PageThumbnail';
import SplitHandle from './SplitHandle';

/** The documents `splits` cuts an N-page strip into, as inclusive page ranges. */
export function documentGroups(pageCount, splits) {
  const starts = [0];
  for (let i = 1; i < pageCount; i++) if (splits.has(i)) starts.push(i);
  return pageCount > 0
    ? starts.map((start, g) => ({ start, end: g + 1 < starts.length ? starts[g + 1] - 1 : pageCount - 1 }))
    : [];
}

const PageStrip = ({
  pages,
  splits,
  selectedPageIndex,
  aiSuggestions,
  renderThumbnail,
  onSelectPage,
  onReorderPages,
  onToggleSplit,
  // Split review (slice 3b): the cuts in `splits` are the splitter's, fixed.
  // No reordering, no toggling, and nothing that looks as though it could.
  // aiSuggestions then carry the proposal's 'high' | 'low' per cut.
  readOnly = false,
  // With readOnly: the pages stay fixed but each gap toggles a cut (boundary
  // review of a held batch, slice 3c-2). onToggleSplit receives the page index.
  cutsEditable = false,
  // Split review rail (slice 3e). Both default off, and with both off the strip
  // renders exactly the DOM and makes exactly the renderThumbnail calls Document
  // Studio always had.
  //
  // groupByDocument: pages are wrapped per document, with a label that stays at
  //   the top of the rail while any of that document's pages are in view.
  // renderWidth: ONE bitmap width in px for every page, passed to
  //   renderThumbnail as its third argument. (3e's { docStart, continuation }
  //   object is gone: every page renders at the same size.)
  groupByDocument = false,
  renderWidth = null,
}) => {
  const [thumbnails, setThumbnails] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const stripRef = useRef(null);

  // Each thumbnail is shown the moment it has rendered, not after the last one.
  // A pass that is superseded (pages reordered, a new render size) stops at its
  // next page rather than racing the pass that replaced it and writing stale
  // bitmaps over newer ones. Until a page's new bitmap arrives its previous one
  // stays on screen.
  useEffect(() => {
    let cancelled = false;
    const loadThumbnails = async () => {
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const url = renderWidth
          ? await renderThumbnail(page.originalIndex, page.rotation, renderWidth)
          : await renderThumbnail(page.originalIndex, page.rotation);
        if (cancelled) return;
        setThumbnails((prev) => (prev[i] === url ? prev : { ...prev, [i]: url }));
      }
    };

    loadThumbnails();
    return () => { cancelled = true; };
  }, [pages, renderThumbnail, renderWidth]);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      onReorderPages(draggedIndex, dragOverIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      onReorderPages(draggedIndex, index);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const getAISuggestion = (beforePageIndex) => {
    return aiSuggestions?.find((s) => s.beforePageIndex === beforePageIndex);
  };

  const renderHandle = (index) => (
    <SplitHandle
      beforePageIndex={index}
      isActive={splits.has(index)}
      isAISuggested={!!getAISuggestion(index)}
      confidence={getAISuggestion(index)?.confidence || 0}
      onToggle={onToggleSplit}
      readOnly={readOnly}
      cutsEditable={cutsEditable}
    />
  );

  const renderItem = (page, index) => (
    <div
      className="ds-page-strip-item"
      onDragOver={readOnly ? undefined : (e) => handleDragOver(e, index)}
      onDrop={readOnly ? undefined : (e) => handleDrop(e, index)}
    >
      <PageThumbnail
        page={page}
        pageIndex={index}
        isSelected={selectedPageIndex === index}
        isDragging={draggedIndex === index}
        thumbnailUrl={thumbnails[index]}
        onSelect={onSelectPage}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        readOnly={readOnly}
      />
    </div>
  );

  if (groupByDocument) {
    // A cut sits BETWEEN two documents, so its handle is outside both groups;
    // every other gap belongs to the document it is inside.
    return (
      <div className="ds-page-strip ds-page-strip--grouped" ref={stripRef}>
        <div className="ds-page-strip-content">
          {documentGroups(pages.length, splits).map((group, g) => {
            const span = group.start === group.end
              ? `Page ${group.start + 1}`
              : `Pages ${group.start + 1}–${group.end + 1}`;
            return (
              <React.Fragment key={group.start}>
                {g > 0 && renderHandle(group.start)}
                <div
                  className={`ds-page-strip-group ds-page-strip-group--${g % 2 === 0 ? 'even' : 'odd'}`}
                  role="group"
                  aria-label={`Document ${g + 1}, ${span.toLowerCase()}`}
                  data-doc-index={g}
                  data-doc-start={group.start}
                >
                  <div className="ds-page-strip-group-label">
                    <span className="ds-page-strip-group-title">Document {g + 1}</span>
                    <span className="ds-page-strip-group-pages">{span}</span>
                  </div>
                  {pages.slice(group.start, group.end + 1).map((page, offset) => {
                    const index = group.start + offset;
                    return (
                      <React.Fragment key={index}>
                        {offset > 0 && renderHandle(index)}
                        {renderItem(page, index)}
                      </React.Fragment>
                    );
                  })}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="ds-page-strip" ref={stripRef}>
      <div className="ds-page-strip-content">
        {pages.map((page, index) => (
          <React.Fragment key={index}>
            {index > 0 && renderHandle(index)}
            {renderItem(page, index)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default PageStrip;
